import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { PrimeNgModule } from "../../../core/shared/primeng.module";
import { SharedComponents } from "../../shared.module";

Chart.register(ChartDataLabels); //add labels on top of each block

/**
 * A reusable component for rendering pie charts or other chart types with dynamic data, using Chart.js.
 * This component supports custom colors, labels, and data, with options for plugins like data labels.
 */
@Component({
  selector: "app-pie-chart",
  standalone: true,
  imports: [PrimeNgModule, CommonModule, ...SharedComponents],
  templateUrl: "./pie-chart.component.html",
  styleUrl: "./pie-chart.component.scss",
})
export class PieChartComponent implements OnChanges {
  @Input() chartType: 'pie' | 'doughnut' = 'pie';
  @Input() isDefect: boolean = false;
  @Input() data: any;
  @Input() backgroundColors: string[] = [];
  @Input() meta: any = {};
  @Input() isLoading: boolean = false;
  @Input() name?: string;
  @Input() showValueOnly: boolean = false;

  testSuites:any =[];
  isSeverity:boolean = false;
  selectedTestSuite:string = "";
  isOverallActive:boolean = false;
  severityColorMap: Record<string, string> = {
    Critical: "#FC404A",
    High:"#FF9999", 
    Major: "#38369A",
    Medium: "#DED6FF",
    Minor: "#5f2cff",
    Low: "#02F576",
    Trivial: "#00434A",
    None: "#A1A4A9"
  };
  
  chartData: any;
  chartOptions: any;
  labels: string[] = [];
  values: number[] = [];
  rootStyles = getComputedStyle(document.documentElement);
  isEmpty: boolean = false;

  constructor() {}

  ngOnInit(): void {
    this.isSeverity = this.meta?.isSeverity;
  if (this.isSeverity) {
    this.testSuites = this.data.map((s: any) => s.suiteName);
    this.showOverallData(); 
  } else {
    this.initializeData();
  }
  }

  /**
   * Lifecycle hook: Called after input properties are set.
   * Updates the chart data if the `data` input changes.
   * @param {SimpleChanges} changes - The changes in input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);
    if (changes["data"]) {
      this.initializeData();
    }
  }

  /**
   * Initializes the chart data and configuration based on input properties.
   * Ensures default colors and formats the data appropriately.
   */
  initializeData() {
    this.initializeColors();
    this.extractLabelsAndValues();
    this.buildChartData();
    this.buildChartOptions();
  }

  private initializeColors(): void {
    this.backgroundColors = this.backgroundColors?.length > 0
      ? this.backgroundColors
      : this.getPieChartColors();
  }

  private extractLabelsAndValues(): void {
    if (Array.isArray(this.data)) {
      this.extractFromArray();
    } else if (Object.keys(this.data).length === 2) {
      this.extractFromTwoKeyObject();
    } else {
      this.extractFromMultiKeyObject();
    }
  }

  private extractFromArray(): void {
    this.labels = this.data.map((item: any) => item.label);
    this.values = this.data.map((item: any) => parseInt(item.value, 10));
  }

  private extractFromTwoKeyObject(): void {
    if (this.isDefect) {
      this.labels = Object.keys(this.data);
      this.values = Object.values(this.data).map((item: any) => item.defect);
    } else {
      this.backgroundColors = this.backgroundColors?.length > 0
        ? this.backgroundColors
        : this.getBackgroundColors();
      this.labels = Object.keys(this.data).map(
        (key) => key.charAt(0).toUpperCase() + key.slice(1)
      );
      this.values = Object.values(this.data);
    }
  }

  private extractFromMultiKeyObject(): void {
    const sortedEntries = Object.entries(this.data)
      .sort((a: any, b: any) => b[1] - a[1])
      .slice(0, 10);

    this.labels = sortedEntries.map(([key, _]) => this.formatLabel(key));
    this.values = sortedEntries.map(([_, value]) => Number(value ?? 0));
  }

  private formatLabel(key: string): string {
    if (key.includes("_") || key.includes("-")) {
      return key
        .split(/[_-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  private buildChartData(): void {
    this.chartData = {
      labels: this.labels,
      datasets: [
        {
          data: this.values,
          backgroundColor: this.backgroundColors,
          hoverBackgroundColor: this.backgroundColors,
          borderWidth: 0,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    this.chartOptions = {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 0, right: 0, top: 0, bottom: 0 }
      },
      cutout: this.chartType === 'doughnut' ? (this.meta?.cutout || '60%') : undefined,
      plugins: {
        legend: this.buildLegendConfig(),
        tooltip: this.buildTooltipConfig(),
        datalabels: this.buildDatalabelsConfig()
      },
    };
  }

  private buildLegendConfig(): any {
    return {
      display: this.meta?.isLegendVisible !== undefined ? this.meta?.isLegendVisible : true,
      position: this.meta?.legendDirection ? this.meta?.legendDirection : "right",
      align: this.meta?.legendPosition === 'bottom' ? 'start' : 'middle',
      labels: {
        useSeriesColors: true,
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: false,
        padding: 20,
        generateLabels: (chart: any) => this.generateLegendLabels(chart),
        filter: (item: any) => item.hidden === false,
      },
    };
  }

  private generateLegendLabels(chart: any): any[] {
    const data = chart.data;
    const dataset = data.datasets[0];
    const total = dataset.data.reduce((acc: any, curr: any) => acc + curr, 0);

    return data.labels.map((label: any, i: number) => {
      const value = dataset.data[i];
      const percentage = Math.round((value / total) * 100);

      return {
        text: this.getLegendText(label, value, percentage),
        fillStyle: dataset.backgroundColor[i],
        hidden: isNaN(value) || value === 0,
        index: i,
        strokeStyle: "transparent",
      };
    });
  }

  private getLegendText(label: string, value: number, percentage: number): string {
    if (this.showValueOnly) {
      return `${label}: ${value}`;
    }
    if (this.meta?.showPercentageInLegend) {
      return `${label} - ${value} (${percentage}%)`;
    }
    return `${label}    ${percentage}%`;
  }

  private buildTooltipConfig(): any {
    return {
      backgroundColor: 'white',
      titleColor: 'black',
      bodyColor: 'black',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      callbacks: {
        title: () => '',
        label: (context: any) => {
          const value = context.raw || 0;
          const label = context.label || '';
          const total = context.chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
          const percentage = Math.round((value / total) * 100);
          return [`${label}: ${value} (${percentage}%)`];
        },
      },
    };
  }

  private buildDatalabelsConfig(): any {
    return {
      display: false,
      color: '#ffffff',
      clip: false,
      formatter: (value: any, context: any) => {},
      font: { weight: 'bold', size: 11 },
      anchor: 'end',
      align: 'start',
      offset: 10,
    };
  }

  /**
   * Retrieves the default set of background colors from CSS variables.
   * @returns {string[]} An array of color values.
   */
  getBackgroundColors() {
    return [
      this.rootStyles.getPropertyValue("--ruby-red").trim(),
      this.rootStyles.getPropertyValue("--bright-green").trim(),
    ];
  }

  getPieChartColors() {
    return [
      this.rootStyles.getPropertyValue("--bright-green").trim()
    ];
  }
  /**
   * Validates if the input data is non-empty.
   * @param {any} obj - The data to validate.
   * @returns {boolean} True if the data is valid; false otherwise.
   */
  isDataValid(obj: any) {
    if (Array.isArray(this.data)) {
      return this.data.length > 0 ? true : false;
    } else {
      return obj && Object.keys(obj).length > 0;
  }
}

onSelectionChange() {
  this.isOverallActive = false;
  const selected = this.data.find((s: any) => s.suiteName === this.selectedTestSuite);
  if (selected) {
    const severityData = {
      critical: selected.critical ?? 0,
      high: selected.high ?? 0,
      major: selected.major ?? 0,
      minor: selected.minor ?? 0,
      medium: selected.medium ?? 0,
      low: selected.low ?? 0,
      trivial: selected.trivial ?? 0,
      none: selected.none ?? 0,
    };
    this.setSeverityChartData(severityData);
  }
}


showOverallData() {
  this.isOverallActive = true;
  const overall = this.calculateOverallSeverity();
  this.setSeverityChartData(overall);
}


calculateOverallSeverity(): Record<string, number> {
  const severity = { critical: 0, high: 0, major: 0, minor: 0, medium: 0, low: 0, trivial: 0, none: 0 };
  this.data.forEach((suite: any) => {
    severity.critical += suite.critical ?? 0;
    severity.high += suite.high ?? 0;
    severity.major += suite.major ?? 0;
    severity.minor += suite.minor ?? 0;
    severity.medium += suite.medium ?? 0;
    severity.low += suite.low ?? 0;
    severity.trivial += suite.trivial ?? 0;
    severity.none += suite.none ?? 0;
  });
  return severity;
}

setSeverityChartData(severityData: Record<string, number>) {
  const labels: string[] = [];
  const values: number[] = [];
  const colors: string[] = [];

  for (const key of Object.keys(severityData)) {
    const value = severityData[key];
    if (value > 0) {
      const label = key.charAt(0).toUpperCase() + key.slice(1); // Capitalize
      labels.push(label);
      values.push(value);
      colors.push(this.severityColorMap[label] || '#ccc');
    }
  }

  this.chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        borderWidth: 0,
      },
    ],
  };
}

}
